import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { 
  UserRound, 
  Receipt, 
  History, 
  Headphones, 
  Wallet2, 
  ArrowUpDown, 
  Settings, 
  LogOut,
  X
} from 'lucide-react';
import HelpIcon from '@/components/icons/HelpIcon';
import { getUID, getSocket } from '@/utils';
import { C } from '@/utils/constants';
import Cookies from 'js-cookie';
import { useScreenSize } from '@/hooks';
import ProfileMenuMobile from './ProfileMenuMobile';
import LogoutModal from './LogoutModal';
import styles from './ProfileMenu.module.css';

const DEFAULT_AVATAR_PATH = '/assets/images/profile-jk.webp';

/**
 * ProfileMenu Component
 * Revamped profile dropdown menu using Radix UI Popover
 * Matches design system color scheme and mobile responsive
 */
const ProfileMenu = () => {
  const [avatarUrl, setAvatarUrl] = useState(DEFAULT_AVATAR_PATH);
  const [open, setOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();

  // Get user info from localStorage or context
  useEffect(() => {
    // TODO: Get avatar from user context/state when available
    const storedAvatar = localStorage.getItem('avatarUrl');
    if (storedAvatar) {
      setAvatarUrl(storedAvatar);
    }
  }, []);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setOpen(false);
  };

  const handleLogoutConfirm = () => {
    // Emit logout to socket if available
    const socket = getSocket();
    if (socket && socket.connected) {
      try {
        socket.emit(C.LOGOUT_USER);
      } catch (error) {
        console.error('Error emitting logout:', error);
      }
    }

    // Clear authentication
    Cookies.remove('token');
    Cookies.remove('uid');
    Cookies.remove('session');
    Cookies.remove('auth');
    localStorage.clear();
    
    // Close modals
    setShowLogoutModal(false);
    setOpen(false);
    
    // Redirect to home
    navigate('/');
    
    // Reload page to reset state
    window.location.reload();
  };

  const handleMenuItemClick = () => {
    setOpen(false);
  };

  const getValidAvatarUrl = (url) => {
    if (!url || typeof url !== 'string') return DEFAULT_AVATAR_PATH;
    if (url.startsWith('https') || url.startsWith('http') || url.startsWith('localhost')) {
      return DEFAULT_AVATAR_PATH;
    }
    return DEFAULT_AVATAR_PATH;
  };

  const menuItems = [
    {
      icon: UserRound,
      label: 'User Information',
      to: '/modal?mode=profile',
      action: handleMenuItemClick,
    },
    {
      icon: Receipt,
      label: 'Transactions',
      to: '/modal?mode=bethistory&tab=transactions',
      action: handleMenuItemClick,
    },
    {
      icon: History,
      label: 'Bet History',
      to: '/modal?mode=bethistory&tab=bets',
      action: handleMenuItemClick,
    },
    // {
    //   icon: Headphones,
    //   label: 'Live Support',
    //   to: '/support',
    //   action: handleMenuItemClick,
    // },
    {
      icon: Wallet2,
      label: 'Wallet',
      to: '/wallet?mode=deposit',
      action: handleMenuItemClick,
    },
    {
      icon: ArrowUpDown,
      label: 'Swap',
      to: '/wallet?mode=swap',
      action: handleMenuItemClick,
    },
    {
      icon: Settings,
      label: 'Global Settings',
      to: '/settings',
      action: handleMenuItemClick,
    },
    {
      icon: HelpIcon,
      label: 'Help',
      to: '/provably-fair',
      action: handleMenuItemClick,
    },
  ];

  // Use separate mobile component
  if (isMobile) {
    return (
      <>
        <button
          className={styles.profileButton}
          aria-label="Profile menu"
          onClick={() => setOpen(true)}
        >
          <img
            src={getValidAvatarUrl(avatarUrl)}
            alt="Profile"
            className={styles.profileImage}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = DEFAULT_AVATAR_PATH;
            }}
          />
        </button>
        <ProfileMenuMobile isOpen={open} onClose={() => setOpen(false)} />
      </>
    );
  }

  // Desktop version with Popover
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          className={styles.profileButton}
          aria-label="Profile menu"
        >
          <img
            src={getValidAvatarUrl(avatarUrl)}
            alt="Profile"
            className={styles.profileImage}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = DEFAULT_AVATAR_PATH;
            }}
          />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className={styles.popoverContent}
          sideOffset={8}
          align="end"
          side="bottom"
          collisionPadding={16}
        >
          <div className={styles.menuList}>
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              const isCustomIcon = item.label === 'Help';
              return (
                <Link
                  key={index}
                  to={item.to}
                  className={styles.menuItem}
                  onClick={item.action}
                >
                  <div className={styles.iconWrapper}>
                    {isCustomIcon ? (
                      <Icon size="18px" />
                    ) : (
                      <Icon size={18} />
                    )}
                  </div>
                  <span className={styles.menuText}>{item.label}</span>
                </Link>
              );
            })}

            <div className={styles.divider}></div>

            <button
              className={styles.menuItem}
              onClick={handleLogoutClick}
            >
              <div className={styles.iconWrapper}>
                <LogOut size={18} />
              </div>
              <span className={styles.menuText}>Logout</span>
            </button>
          </div>

          <Popover.Arrow className={styles.popoverArrow} />
        </Popover.Content>
      </Popover.Portal>

      {/* Logout Confirmation Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
      />
    </Popover.Root>
  );
};

export default ProfileMenu;
